import React, { useState, useRef, useEffect } from 'react';
import {
  MicrophoneIcon,
  StopIcon,
  PlayIcon,
  PauseIcon,
  SparklesIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../ui/button';

/**
 * VoicePracticeRecorder
 * Provides real-time microphone recording, playback, and live German speech-to-text
 * recognition for Goethe B2 Sprechen practice.
 */
const VoicePracticeRecorder = ({ onTranscriptChange, targetMinutes = 2 }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [micSupported, setMicSupported] = useState(true);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialize Speech Recognition if supported
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'de-DE';

      recognition.onresult = (event) => {
        let currentTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          currentTranscript += event.results[i][0].transcript + ' ';
        }
        setTranscript(currentTranscript.trim());
        if (onTranscriptChange) onTranscriptChange(currentTranscript.trim());
      };

      recognition.onerror = (e) => {
        console.warn('Speech recognition warning:', e.error);
        setIsRecognizing(false);
      };

      recognition.onend = () => {
        setIsRecognizing(false);
      };

      recognitionRef.current = recognition;
    }
  }, [onTranscriptChange]);

  const startRecording = async () => {
    try {
      audioChunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(200);
      setIsRecording(true);
      setRecordingTime(0);
      setTranscript('');

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      // Start recognition if available
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
          setIsRecognizing(true);
        } catch {
          // already started or failed
        }
      }
    } catch (err) {
      console.error('Microphone access denied:', err);
      setMicSupported(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);

      if (recognitionRef.current && isRecognizing) {
        try {
          recognitionRef.current.stop();
        } catch {}
        setIsRecognizing(false);
      }
    }
  };

  const togglePlayback = () => {
    if (!audioPlayerRef.current) return;
    if (isPlaying) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    } else {
      audioPlayerRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${mins.toString().padStart(2, '0')}:${rem.toString().padStart(2, '0')}`;
  };

  const wordCount = transcript ? transcript.split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200/80 shadow-sm p-6 space-y-5">
      {!micSupported && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5 rounded-xl text-xs">
          ⚠️ Mikrofoni nuk është i disponueshëm ose nuk ka leje. Ju lutem aktivizoni lejen e mikrofonit në shfletues.
        </div>
      )}
      <div className="flex items-center justify-between border-b border-zinc-100 pb-4">
        <div>
          <h3 className="font-bold text-zinc-900 flex items-center gap-2 text-base">
            <MicrophoneIcon className="w-5 h-5 text-orange-500" />
            Regjistruesi Inteligjent i Zërit
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            Flisni në gjermanisht. Shfletuesi do të regjistrojë audion dhe do të shkruajë fjalët tuaja.
          </p>
        </div>

        {/* Live Status indicator */}
        {isRecording && (
          <div className="flex items-center gap-2 bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-semibold animate-pulse">
            <span className="w-2 h-2 rounded-full bg-red-600" />
            LIVE REC: {formatTime(recordingTime)}
          </div>
        )}
      </div>

      {/* Main recording controls */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 py-3">
        {!isRecording ? (
          <Button
            onClick={startRecording}
            className="h-12 px-6 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold shadow-lg shadow-orange-500/20 gap-2 text-sm"
          >
            <MicrophoneIcon className="w-5 h-5" />
            {audioUrl ? 'Regjistro Përsëri' : 'Fillo Regjistrimin'}
          </Button>
        ) : (
          <Button
            onClick={stopRecording}
            variant="destructive"
            className="h-12 px-6 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-500/20 gap-2 text-sm"
          >
            <StopIcon className="w-5 h-5" />
            Ndalo Regjistrimin ({formatTime(recordingTime)})
          </Button>
        )}

        {audioUrl && !isRecording && (
          <div className="flex items-center gap-3">
            <Button
              onClick={togglePlayback}
              variant="outline"
              className="h-12 px-5 rounded-xl border-zinc-300 font-bold gap-2 text-sm"
            >
              {isPlaying ? <PauseIcon className="w-5 h-5 text-zinc-700" /> : <PlayIcon className="w-5 h-5 text-orange-600" />}
              {isPlaying ? 'Pauzë' : 'Dëgjo Veten'}
            </Button>
            <audio
              ref={audioPlayerRef}
              src={audioUrl}
              onEnded={() => setIsPlaying(false)}
              className="hidden"
            />
          </div>
        )}
      </div>

      {/* Real-time German Speech-to-Text Feedback */}
      <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-200/70 space-y-2">
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span className="font-semibold uppercase tracking-wider flex items-center gap-1.5 text-zinc-700">
            <SparklesIcon className="w-4 h-4 text-amber-500" />
            Transkriptimi i Drejtpërdrejtë (Zëri në Tekst):
          </span>
          <span className="font-bold text-zinc-600">
            {wordCount} fjalë të thëna
          </span>
        </div>

        <div className="min-h-[70px] max-h-36 overflow-y-auto text-sm text-zinc-800 bg-white p-3 rounded-lg border border-zinc-200">
          {transcript ? (
            <p className="leading-relaxed font-sans">{transcript}</p>
          ) : isRecording ? (
            <p className="text-zinc-400 italic text-xs flex items-center gap-2">
              <SpeakerWaveIcon className="w-4 h-4 text-orange-500 animate-pulse" />
              Po ju dëgjojmë... Flisni qartë në gjermanisht.
            </p>
          ) : (
            <p className="text-zinc-400 text-xs italic">
              Fjalët e shqiptuara do të shfaqen këtu automatikisht sapo të flisni.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoicePracticeRecorder;
